import {
    isoAndUnixZodSchema,
    nowIsoAndUnix,
    type IsoAndUnixTimestamp,
} from "@@/shared/date-time/iso-and-unix";
import type { OrderRefundRequestedEvent } from "@@/shared/orders/order-events/events/_order-refund-requested.event";
import { isOrderChangeRefundRequestedEvent } from "@@/shared/orders/order-events/events/order-change-refund-requested.event";
import {
    orderChangedEventZodSchema,
    type OrderChangedEvent,
} from "@@/shared/orders/order-events/events/order-changed-event/order-changed.event";
import type {
    OrderCreatedEvent} from "@@/shared/orders/order-events/events/order-created.event";
import {
    orderCreatedEventZodSchema,
} from "@@/shared/orders/order-events/events/order-created.event";
import { orderFullRefundRequestedEventZodSchema } from "@@/shared/orders/order-events/events/order-full-refund-requested.event";
import { orderPartialRefundRequestedEventZodSchema } from "@@/shared/orders/order-events/events/order-partial-refund-requested.event";
import {
    orderPaymentRefundFulfilledEventZodSchema,
    type OrderPaymentRefundFulfilledEvent,
} from "@@/shared/orders/order-events/events/order-payment-events/order-payment-refund-fulfilled.event";
import { orderIdZodSchema, type OrderId } from "@@/shared/orders/order-id";
import { absPrice, negatePrice } from "@@/shared/prices/negate-price";
import { priceZodSchema, type Price } from "@@/shared/prices/price";
import { z } from "zod";
import { parseFulfillmentDate } from "../parse-fulfillment-date";
import {
    settlementIdFactory,
    settlementIdZodSchema,
    type SettlementId,
} from "../settlement-id";
import {
    settlementPaymentTypeZodEnum,
    type SettlementPaymentType,
} from "./settlement-payment-type";

type RefundSettlement = {
    readonly _id: SettlementId;
    readonly _createdAt: IsoAndUnixTimestamp;
    readonly _type: "SETTLEMENT";
    readonly type: "REFUND";
    readonly paymentType: SettlementPaymentType;
    readonly orderId: OrderId;
    readonly originEvent: OrderCreatedEvent | OrderChangedEvent;
    readonly refundRequestedEvent: OrderRefundRequestedEvent;
    readonly settlementEvent: OrderPaymentRefundFulfilledEvent;
    readonly price: Price;
    readonly orderConfirmedFulfillmentDate?: IsoAndUnixTimestamp;
    readonly originEventFulfillmentDate: IsoAndUnixTimestamp;
};

const refundSettlementZodObject = z.object({
    _id: settlementIdZodSchema,
    _createdAt: isoAndUnixZodSchema,
    _type: z.literal("SETTLEMENT"),
    type: z.literal("REFUND"),
    paymentType: settlementPaymentTypeZodEnum,
    orderId: orderIdZodSchema,
    originEvent: z.union([
        orderCreatedEventZodSchema,
        orderChangedEventZodSchema,
    ]),
    refundRequestedEvent: z.union([
        orderFullRefundRequestedEventZodSchema,
        orderPartialRefundRequestedEventZodSchema,
    ]),
    settlementEvent: orderPaymentRefundFulfilledEventZodSchema,
    price: priceZodSchema,
    orderConfirmedFulfillmentDate: isoAndUnixZodSchema.optional(),
    originEventFulfillmentDate: isoAndUnixZodSchema,
});
const refundSettlementZodSchema: z.ZodSchema<RefundSettlement> =
    refundSettlementZodObject;

const isLegacyPartialRefund = (params: {
    originEvents: Array<OrderCreatedEvent | OrderChangedEvent>;
    settlementEvent: OrderPaymentRefundFulfilledEvent;
}) => {
    return (
        params.originEvents.length === 1 &&
        params.originEvents[0].type === "CREATED" &&
        params.settlementEvent.refundedAmount.amountIncludingVat !==
            params.originEvents[0].order.totalPrice.amountIncludingVat
    );
};

const refundSettlementFactory = (params: {
    readonly originEvents: Array<OrderChangedEvent | OrderCreatedEvent>;
    readonly refundRequestedEvent: OrderRefundRequestedEvent;
    readonly paymentType: SettlementPaymentType;
    readonly settlementEvent: OrderPaymentRefundFulfilledEvent;
    readonly orderConfirmedFulfillmentDate: IsoAndUnixTimestamp | undefined;
    readonly previousLegacyPartialRefundsPrice: Price;
    readonly orderCreatedOriginEvent: OrderCreatedEvent;
}): RefundSettlement[] => {
    // const fullRefundPrice = negatePrice(params.settlementEvent.refundedAmount);

    // If the refund is a legacy partial refund then there can
    // be only one origin event and that's an order created event
    if (
        isLegacyPartialRefund(params) &&
        !isOrderChangeRefundRequestedEvent(params.refundRequestedEvent)
    ) {
        // Probably a partial refund for unknown content
        // Create a settlement for the partial refund
        return [
            {
                _id: settlementIdFactory(),
                _createdAt: nowIsoAndUnix(),
                _type: "SETTLEMENT",
                type: "REFUND",
                orderId: params.originEvents[0].orderId,
                price: negatePrice(
                    absPrice(params.settlementEvent.refundedAmount),
                ),
                orderConfirmedFulfillmentDate:
                    params.orderConfirmedFulfillmentDate,
                originEventFulfillmentDate: parseFulfillmentDate(
                    params.originEvents[0],
                ),
                originEvent: params.orderCreatedOriginEvent,
                paymentType: params.paymentType,
                refundRequestedEvent: params.refundRequestedEvent,
                settlementEvent: params.settlementEvent,
            },
        ];
    }

    // Create a settlement for each origin event
    const settlements = params.originEvents.map(
        (originEvent): RefundSettlement => {
            return {
                _id: settlementIdFactory(),
                _createdAt: nowIsoAndUnix(),
                _type: "SETTLEMENT",
                type: "REFUND",
                orderId: originEvent.orderId,
                price: negatePrice(params.settlementEvent.refundedAmount),
                //price: negatePrice(absPrice(originPriceRefundsSubtracted)),
                originEvent,
                orderConfirmedFulfillmentDate:
                    params.orderConfirmedFulfillmentDate,
                originEventFulfillmentDate: parseFulfillmentDate(originEvent),
                paymentType: params.paymentType,
                refundRequestedEvent: params.refundRequestedEvent,
                settlementEvent: params.settlementEvent,
            };
        },
    );

    // const calculatedFullRefundPrice = sumPrices(
    //     settlements.map(settlement => settlement.price),
    // );
    // if (
    //     fullRefundPrice.amountIncludingVat !==
    //     calculatedFullRefundPrice.amountIncludingVat
    // ) {
    //     throw new Error(
    //         `Total price of settlements does not match full price of refund; ${fullRefundPrice.amountIncludingVat} !== ${calculatedFullRefundPrice.amountIncludingVat}; ${params.settlementEvent.orderId}`,
    //     );
    // }

    return settlements;
};

export {
    refundSettlementFactory,
    refundSettlementZodObject,
    refundSettlementZodSchema,
};
export type { RefundSettlement };
